import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyMohrMode({ brand }: ModeRendererProps) {
  // Cubic permutations; code revealing structure
  // Hypercubes, combinatorics, line permutations
  
  // Helper to draw hypercube projection
  const drawHypercube = (cx: number, cy: number, size: number, rotation: number) => {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    
    // 4D hypercube vertices projected to 2D
    const vertices = [];
    for (let i = 0; i < 16; i++) {
      const x = ((i & 1) ? 1 : -1) * size;
      const y = ((i & 2) ? 1 : -1) * size;
      const z = ((i & 4) ? 1 : -1) * size * 0.5;
      const w = ((i & 8) ? 1 : -1) * size * 0.3;
      
      // Simple 4D to 2D projection with rotation
      const px = cx + (x * cos - y * sin) + z * 0.5 + w * 0.3;
      const py = cy + (x * sin + y * cos) + z * 0.3 + w * 0.2;
      vertices.push({ x: px, y: py });
    }
    
    return vertices;
  };
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Central hypercube */}
      <g>
        {(() => {
          const vertices = drawHypercube(397, 280, 80, 0.3);
          const edges = [
            // Inner cube
            [0, 1], [1, 3], [3, 2], [2, 0],
            [4, 5], [5, 7], [7, 6], [6, 4],
            [0, 4], [1, 5], [2, 6], [3, 7],
            // Outer cube
            [8, 9], [9, 11], [11, 10], [10, 8],
            [12, 13], [13, 15], [15, 14], [14, 12],
            [8, 12], [9, 13], [10, 14], [11, 15],
            // Connections
            [0, 8], [1, 9], [2, 10], [3, 11],
            [4, 12], [5, 13], [6, 14], [7, 15],
          ];
          
          return edges.map(([i, j], idx) => (
            <line
              key={`hypercube-${idx}`}
              x1={vertices[i].x}
              y1={vertices[i].y}
              x2={vertices[j].x}
              y2={vertices[j].y}
              stroke={brand.deep}
              strokeWidth="2"
              opacity={0.7}
            />
          ));
        })()}
      </g>
      
      {/* Line permutations - Mohr's signature style */}
      {Array.from({ length: 12 }).map((_, perm) => {
        const x = 100 + (perm % 4) * 160;
        const y = 580 + Math.floor(perm / 4) * 160;
        const size = 120;
        
        // Different line permutations
        const lines = [];
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + (perm * Math.PI / 12);
          const x1 = x + size / 2 + Math.cos(angle) * 20;
          const y1 = y + size / 2 + Math.sin(angle) * 20;
          const x2 = x + size / 2 + Math.cos(angle + Math.PI) * 55;
          const y2 = y + size / 2 + Math.sin(angle + Math.PI) * 55;
          
          lines.push(
            <line
              key={`perm-${perm}-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={brand.violet}
              strokeWidth="1.5"
              opacity={0.6}
            />
          );
        }
        
        return <g key={`perm-group-${perm}`}>{lines}</g>;
      })}
      
      {/* Combinatoric grid */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 12 }).map((_, col) => {
          const x = 80 + col * 56;
          const y = 80 + row * 56;
          const combination = (row * 12 + col) % 16;
          
          // Binary representation as lines
          if (combination & 1) {
            <line x1={x} y1={y} x2={x + 50} y2={y} stroke={brand.aqua} strokeWidth="1" />;
          }
          if (combination & 2) {
            <line x1={x + 50} y1={y} x2={x + 50} y2={y + 50} stroke={brand.aqua} strokeWidth="1" />;
          }
          if (combination & 4) {
            <line x1={x + 50} y1={y + 50} x2={x} y2={y + 50} stroke={brand.aqua} strokeWidth="1" />;
          }
          if (combination & 8) {
            <line x1={x} y1={y + 50} x2={x} y2={y} stroke={brand.aqua} strokeWidth="1" />;
          }
          
          return (
            <rect
              key={`combo-${row}-${col}`}
              x={x}
              y={y}
              width="50"
              height="50"
              fill="none"
              stroke={brand.deep}
              strokeWidth="0.5"
              opacity={0.3}
            />
          );
        })
      )}
      
      {/* Cubic rotation sequence */}
      {Array.from({ length: 6 }).map((_, i) => {
        const cx = 120 + i * 110;
        const cy = 1000;
        const rotation = i * Math.PI / 6;
        const size = 40;
        
        // Simple cube
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        
        return (
          <g key={`cube-${i}`}>
            {/* Front face */}
            <rect
              x={cx - size / 2}
              y={cy - size / 2}
              width={size}
              height={size}
              fill="none"
              stroke={brand.violet}
              strokeWidth="2"
              opacity={0.7}
            />
            {/* Back face with rotation */}
            <rect
              x={cx - size / 2 + cos * 20}
              y={cy - size / 2 + sin * 20}
              width={size}
              height={size}
              fill="none"
              stroke={brand.aqua}
              strokeWidth="1.5"
              opacity={0.5}
            />
            {/* Connecting edges */}
            <line
              x1={cx - size / 2}
              y1={cy - size / 2}
              x2={cx - size / 2 + cos * 20}
              y2={cy - size / 2 + sin * 20}
              stroke={brand.amber}
              strokeWidth="1"
              opacity={0.6}
            />
          </g>
        );
      })}
      
      {/* Vasarely optical grid overlay */}
      {Array.from({ length: 8 }).map((_, i) => {
        const size = 200 - i * 22;
        const offset = i * 11;
        return (
          <rect
            key={`optical-${i}`}
            x={297 + offset}
            y={180 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={brand.aqua}
            strokeWidth="1"
            opacity={0.2 - i * 0.02}
          />
        );
      })}
    </g>
  );
}
